import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import * as Accordion from '@radix-ui/react-accordion';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useMatch, useParams } from 'react-router';
import { toast } from 'sonner';

import Header from '../Header';
import useOutletContext from '../useOutletContext';

import ComboBox from '@/components/ComboBox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import NumberField from '@/components/NumberField';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface GetDebtSecurities {
  debtSecurities: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
}

interface GetEquitySecurities {
  equitySecurities: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
}

interface GetIndustries {
  rvaCalculatorIndustries: {
    label: null | string;
    value: null | string;
  }[];
}

interface GetOpportunityForRvaCalculator {
  node: {
    __typename: 'OpportunityNode';
    baseRate: null | string;
    borrowingBase: null | string;
    equityAsPercentOfSmInvested: null | number;
    equityAsPercentOfTotalCapital: null | number;
    id: string;
    leverageDetachmentPoint: null | string;
    name: null | string;
    originalIssueDiscountClosingFee: null | number;
    postFirstOutSofrCashSpread: null | number;
    preFirstOutSofrCashSpread: null | number;
    projectedDebtIrr: null | number;
    projectedDebtMoic: null | number;
    projectedEquityIrr: null | number;
    projectedTotalIrr: null | number;
    projectedTotalMoic: null | number;
    projectName: null | string;
    securityIds:
      | {
          __typename: 'DealOpportunitySecurityNode';
          category: null | string;
          id: string;
          lien: null | number;
          securityType: null | string;
        }[]
      | null;
    smcIndustry: null | string;
    smProjectedDebtInvestment: null | number;
    smProjectedEquityInvestment: null | number;
    totalDealSize: null | number;
    totalProposedDebtInvestment: null | number;
    totalProposedEquityInvestment: null | number;
    totalSmHold: null | number;
  };
}

const GET_OPPORTUNITY_FOR_RVA_CALCULATOR = gql`
  query GetOpportunityForRVACalculator($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        projectName
        # Industry
        smcIndustry
        # Financial Rate Inputs (direct mapping to RVA Calculator)
        baseRate
        borrowingBase
        preFirstOutSofrCashSpread
        postFirstOutSofrCashSpread
        originalIssueDiscountClosingFee
        # Projected Returns
        projectedTotalMoic
        projectedTotalIrr
        projectedDebtMoic
        projectedDebtIrr
        projectedEquityIrr
        # Opportunity Size
        totalProposedDebtInvestment
        totalProposedEquityInvestment
        smProjectedDebtInvestment
        smProjectedEquityInvestment
        totalDealSize
        totalSmHold
        # Capital Structure
        equityAsPercentOfSmInvested
        equityAsPercentOfTotalCapital
        leverageDetachmentPoint
        # Securities (for security type details)
        securityIds {
          id
          securityType
          category
          lien
        }
      }
    }
  }
`;

const GET_EQUITY_SECURITIES = gql`
  query debtSecurity {
    equitySecurities: getFieldSelections(
      model: "opstream.rva.calculator"
      fieldName: "equity_security_type"
    ) {
      id
      name
      value
    }
  }
`;

const GET_DEBT_SECURITIES = gql`
  query debtSecurity {
    debtSecurities: getFieldSelections(
      model: "opstream.rva.calculator"
      fieldName: "debt_security_type"
    ) {
      id
      name
      value
    }
  }
`;

const GET_INDUSTRIES = gql`
  query GetIndustries {
    rvaCalculatorIndustries {
      value
      label
    }
  }
`;

const CREATE_RVA_CALCULATOR = gql`
  mutation CreateRVACalculator(
    $name: String!
    $transactionName: String
    $industry: String!
    $opId: ID!
    $industryPercentPlug: Float!
    $equityPercentPlug: Float!
    $pikRate: Float!
    $totalDebtOpportunitySize: Float!
    $totalEquityOpportunitySize: Float!
    $debtSecurityType: String!
    $equitySecurityType: String!
    $postFirstOutSofrCashSpread: Float!
    $originalIssueDiscountClosingFee: Float!
    $projectedTotalMoic: Float!
    $projectedTotalIrr: Float!
    $grossUnleveredDebtIrr: Float!
    $grossUnleveredEquityIrr: Float!
    $grossUnleveredBlendedIrr: Float!
    $grossUnleveredCashYieldBlended: Float!
  ) {
    createRvaCalculator(
      input: {
        name: $name
        transactionName: $transactionName
        industry: $industry
        opportunityId: $opId
        industryPercentPlug: $industryPercentPlug
        equityPercentPlug: $equityPercentPlug
        pikRate: $pikRate
        totalDebtOpportunitySize: $totalDebtOpportunitySize
        totalEquityOpportunitySize: $totalEquityOpportunitySize
        debtSecurityType: $debtSecurityType
        equitySecurityType: $equitySecurityType
        postFirstOutSofrCashSpread: $postFirstOutSofrCashSpread
        originalIssueDiscountClosingFee: $originalIssueDiscountClosingFee
        projectedTotalMoic: $projectedTotalMoic
        projectedTotalIrr: $projectedTotalIrr
        grossUnleveredDebtIrr: $grossUnleveredDebtIrr
        grossUnleveredEquityIrr: $grossUnleveredEquityIrr
        grossUnleveredBlendedIrr: $grossUnleveredBlendedIrr
        grossUnleveredCashYieldBlended: $grossUnleveredCashYieldBlended
      }
    ) {
      ok
      message
      calculator {
        id
        name
        debtPercentOfInvestedCapital
        equityPercentOfInvestedCapital
        totalOpportunitySize
        industry
        concentrationLimits {
          id
          limitType
          fund {
            name
          }
          lowEnd
          highEnd
        }
      }
    }
  }
`;

const COMPUTE_FUND_SPECIFIC_RVA = gql`
  mutation ComputeFundSpecificRVA($calcId: ID!) {
    computeFundSpecificRvaCalculator(calculatorId: $calcId) {
      ok
      message
    }
  }
`;

const UPDATE_CONCENTRATION_LIMIT_LOW_END = gql`
  mutation UpdateConcentrationLimit(
    $calculatorId: ID!
    $fundId: ID!
    $limitType: String!
    $lowEnd: Float!
    $id: ID!
  ) {
    updateRvaCalculatorConcentrationLimit(
      id: $id
      input: {
        lowEnd: $lowEnd
        calculatorId: $calculatorId
        fundId: $fundId
        limitType: $limitType
      }
    ) {
      ok
      message
      concentrationLimit {
        id
        lowEnd
        highEnd
        testIndustryConcentrationPreAllocation
      }
    }
  }
`;

const UPDATE_CONCENTRATION_LIMIT_HIGH_END = gql`
  mutation UpdateConcentrationLimit(
    $calculatorId: ID!
    $fundId: ID!
    $limitType: String!
    $highEnd: Float!
    $id: ID!
  ) {
    updateRvaCalculatorConcentrationLimit(
      id: $id
      input: {
        highEnd: $highEnd
        calculatorId: $calculatorId
        fundId: $fundId
        limitType: $limitType
      }
    ) {
      ok
      message
      concentrationLimit {
        id
        lowEnd
        highEnd
        testIndustryConcentrationPreAllocation
      }
    }
  }
`;

interface GetRvaCalculatorWithConcentrationLimits {
  rvaCalculator: {
    __typename: 'RVACalculatorNode';
    concentrationLimits:
      | {
          actualConcentration: null | number;
          fund: {
            id: string;
            name: null | string;
          } | null;
          highEnd: null | number;
          id: string;
          industry: null | string;
          industryCushionToHighEnd: null | number;
          limitType: null | string;
          lowEnd: null | number;
          testAccretiveDebtEquityMix: null | string;
          testDebtEquityDetail: null | string;
          testEquityConcentration: null | string;
          testIndustryConcentrationPreAllocation: null | string;
        }[]
      | null;
    id: string;
    industry: null | string;
    name: null | string;
  };
}

const GET_RVA_CALCULATOR_WITH_CONCENTRATION_LIMITS = gql`
  query GetRVACalculatorWithConcentrationLimits($id: ID!) {
    rvaCalculator(id: $id) {
      id
      name
      industry
      # Concentration Limits (industry, debt, equity per fund)
      concentrationLimits {
        id
        fund {
          id
          name
        }
        limitType # "industry", "debt", or "equity"
        industry # Only set for industry type
        actualConcentration # Current concentration %
        lowEnd # Low end of acceptable range
        highEnd # High end of acceptable range
        industryCushionToHighEnd # Cushion to high end %
        testIndustryConcentrationPreAllocation # "Clear", "Consider", "Fail", "n/a"
        testEquityConcentration # For equity type
        testAccretiveDebtEquityMix # Accretive mix test
        testDebtEquityDetail # D/E detail
      }
    }
  }
`;

interface GetRvaCalculator {
  rvaCalculator: {
    __typename: 'RVACalculatorNode';
    baseRate: null | string;
    borrowingBase: null | string;
    calculatorAssumptions: [];
    calculatorFundAnalyses: {
      benefitsOfLeverage: null | number;
      carryPaid: null | number;
      costsOfLeverage: null | number;
      currentDebtConcentration: null | number;
      currentEquityConcentration: null | number;
      currentIndustryConcentration: null | number;
      debtToEquityRatio: null | number;
      effectiveLeveredCashYieldOnTotalCapital: null | number;
      effectiveUnleveredCashYieldOnDebt: null | number;
      effectiveUnleveredCashYieldOnTotalCapital: null | number;
      effYieldPerUnitLev: null | number;
      fund: {
        ablSpread: null | number;
        adminExpenses: null | number;
        carriedInterest: null | number;
        hurdleRate: null | number;
        id: string;
        leverageCost: null | number;
        leverageDebtToTotalAssets: null | number;
        managementFees: null | number;
        name: string;
      };
      id: string;
      leveredYieldPostCarry: null | number;
      netAssetYieldPreHurdle: null | number;
      netIrrLevered: null | number;
      netMoicLevered: null | number;
      netYieldPreHurdle: null | number;
      postFirstOutCashInterestRate: null | number;
      preFirstOutCashInterestRate: null | number;
      testAboveFundExpenses: null | string;
      testAboveFundHurdleRate: null | string;
      testEffectiveLeveredCashYield: null | string;
      testNetIncomeGeneration: null | string;
      testPostFoCashInterestRate: null | string;
      testPostFoSofrCashSpread: null | string;
      testRelativeValue: null | string;
      testTotalIrrGrossUnlevered: null | string;
      testTotalIrrNetLevered: null | string;
    }[];
    concentrationLimits: {
      actualConcentration: null | number;
      fund: {
        id: string;
        name: string;
      };
      highEnd: null | number;
      id: string;
      industry: null | string;
      industryCushionToHighEnd: null | number;
      limitType: string;
      lowEnd: null | number;
      testAccretiveDebtEquityMix: null | string;
      testDebtEquityDetail: null | string;
      testEquityConcentration: null | string;
      testIndustryConcentrationPreAllocation: null | string;
    }[];
    debtPercentOfInvestedCapital: null | number;
    equityPercentOfInvestedCapital: null | number;
    equityPercentPlug: null | number;
    grossUnleveredBlendedIrr: null | number;
    grossUnleveredCashYieldBlended: null | number;
    grossUnleveredDebtIrr: null | number;
    grossUnleveredEquityIrr: null | number;
    id: string;
    industry: null | string;
    industryPercentPlug: null | number;
    name: null | string;
    postFirstOutSofrCashSpread: null | number;
    preFirstOutSofrCashSpread: null | number;
    projectedTotalMoic: null | number;
    totalIrrNetLevered: null | number;
    totalMoicNetLevered: null | number;
    totalOpportunitySize: null | number;
    transactionName: null | string;
    useCashTransactions: boolean | null;
  };
}

interface ListRvaCalculators {
  rvaCalculators: {
    __typename: 'RVACalculatorNode';
    calculatorFundAnalyses: {
      fund: {
        name: string;
      };
      testRelativeValue: null | string;
    }[];
    createDate: null | string;
    createUid: {
      id: string;
      name: null | string;
    } | null;
    id: string;
    industry: null | string;
    name: null | string;
    totalIrrNetLevered: null | number;
    totalMoicNetLevered: null | number;
    transactionName: null | string;
  }[];
}

const LIST_RVA_CALCULATORS = gql`
  query ListRVACalculators($opportunityId: ID) {
    rvaCalculators(opportunityId: $opportunityId) {
      ... on RVACalculatorNode {
        id
        name
        transactionName
        industry
        createUid {
          id
          name
        }
        createDate
        totalIrrNetLevered
        totalMoicNetLevered
        calculatorFundAnalyses {
          fund {
            name
          }
          testRelativeValue
        }
      }
    }
  }
`;

const GET_RVA_CALCULATOR = gql`
  query GetRVACalculator($id: ID!) {
    rvaCalculator(id: $id) {
      id
      name
      transactionName
      industry
      useCashTransactions
      industryPercentPlug
      equityPercentPlug
      # Computed Opportunity Size
      totalOpportunitySize
      debtPercentOfInvestedCapital
      equityPercentOfInvestedCapital
      # Deal Projections
      baseRate
      borrowingBase
      preFirstOutSofrCashSpread
      postFirstOutSofrCashSpread
      # Gross Unlevered Inputs
      grossUnleveredDebtIrr
      grossUnleveredEquityIrr
      grossUnleveredBlendedIrr
      grossUnleveredCashYieldBlended
      # Summary
      totalIrrNetLevered
      totalMoicNetLevered
      projectedTotalMoic
      # Fund Analyses (computed per fund)
      calculatorFundAnalyses {
        id
        fund {
          id
          name
          managementFees
          adminExpenses
          carriedInterest
          hurdleRate
          leverageDebtToTotalAssets
          ablSpread
          leverageCost
        }
        # Return Metrics
        netIrrLevered
        netMoicLevered
        # Cash Interest Rates
        preFirstOutCashInterestRate
        postFirstOutCashInterestRate
        # Effective Yields
        effectiveUnleveredCashYieldOnDebt
        effectiveUnleveredCashYieldOnTotalCapital
        effectiveLeveredCashYieldOnTotalCapital
        effYieldPerUnitLev
        # Leverage Metrics
        debtToEquityRatio
        benefitsOfLeverage
        costsOfLeverage
        netAssetYieldPreHurdle
        netYieldPreHurdle
        carryPaid
        leveredYieldPostCarry
        # Concentration (from fund exposures)
        currentIndustryConcentration
        currentDebtConcentration
        currentEquityConcentration
        # TEST RESULTS
        testAboveFundExpenses # Clear/Fail
        testNetIncomeGeneration # Clear/Fail
        testAboveFundHurdleRate # Clear/Fail
        testPostFoSofrCashSpread # Clear/Fail
        testPostFoCashInterestRate # Clear/Fail
        testEffectiveLeveredCashYield # Clear/Fail
        testTotalIrrGrossUnlevered # Clear/Fail
        testTotalIrrNetLevered # Clear/Fail
        testRelativeValue # "X/5"
      }
      # Concentration Limits (per fund)
      concentrationLimits {
        id
        fund {
          id
          name
        }
        limitType # industry, debt, equity
        industry
        lowEnd
        highEnd
        actualConcentration
        industryCushionToHighEnd
        # TEST RESULTS
        testIndustryConcentrationPreAllocation # Clear/Fail/Consider
        testEquityConcentration # Clear/Fail/Consider
        testAccretiveDebtEquityMix # Yes/No/Consider
        testDebtEquityDetail # Clear/Debt Overconcentration/Equity Overconcentration/n/a
      }
      # Calculator Assumptions (levered/unlevered per fund)
      calculatorAssumptions {
        id
        name
        assumptionType # levered/unlevered
        fund {
          id
          name
        }
        # Computed Values
        grossWeightedAverageLife
        realizedProceeds
        proceedsPlusFairValue
        leverageAmt
        leverageExpense
        managementFees
        fundExp
        netFunded
        preCarryNetProfitsLosses
        hurdle
        carryCheck
        carry
        netProfits
        # Return Values
        leveragedNetMoic
        leveragedNetIrr
        proceeds
        unleveragedNetMoic
        unleveragedNetIrr
        # Leverage Analysis
        netAssetYieldPreHurdle
        debtToEquityRatio
        benefitsOfLeverage
        costsOfLeverage
        netYieldPreHurdle
        carryPaid
        leveredYieldPostCarry
        # Test Results
        testAboveFundExpenses
        testNetIncomeGeneration
        testAboveFundHurdleRate
      }
    }
  }
`;

interface GetSofr {
  currentSofrRates: {
    __typename: 'SofrRateNode';
    effectiveDate: null | string;
    id: string;
    isCurrent: boolean | null;
    lastFetchTime: null | string;
    name: null | string;
    rate1m: null | number;
    rate3m: null | number;
    sourceUrl: null | string;
  };
}

const GET_SOFR = gql`
  query sof {
    currentSofrRates {
      id
      name
      effectiveDate
      rate1m
      rate3m
      sourceUrl
      lastFetchTime
      isCurrent
    }
  }
`;

const fundNameToSlug = (name: string) =>
  name.toLowerCase().replace(/ - /g, '-').replace(/ /g, '-');

const buildPortfolioInputFromLimits = (
  limits: {
    fund: { name: string };
    highEnd: null | number;
    limitType: string;
    lowEnd: null | number;
  }[]
) => {
  const initial: Record<string, string | undefined> = {};
  for (const limit of limits) {
    const slug = fundNameToSlug(limit.fund.name);
    if (limit.limitType === 'Industry Concentration') {
      initial[`${slug}-current-concentration-range-low-end-field`] =
        limit.lowEnd?.toString();
      initial[`${slug}-current-concentration-range-high-end-field`] =
        limit.highEnd?.toString();
    } else if (limit.limitType === 'Debt Concentration') {
      initial[`${slug}-debt-current-concentration-range-low-end-field`] =
        limit.lowEnd?.toString();
      initial[`${slug}-debt-current-concentration-range-high-end-field`] =
        limit.highEnd?.toString();
    } else if (limit.limitType === 'Equity Concentration') {
      initial[`${slug}-equity-current-concentration-range-low-end-field`] =
        limit.lowEnd?.toString();
      initial[`${slug}-equity-current-concentration-range-high-end-field`] =
        limit.highEnd?.toString();
    }
  }
  return initial;
};

interface DataTableRow {
  [key: string]: boolean | string | undefined;
  isSection: boolean;
}

const DataTable = ({
  columns,
  label,
  labelKey,
  rows
}: {
  columns: string[];
  label: string;
  labelKey: string;
  rows: DataTableRow[];
}) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="h-10 min-w-[243px] text-xs font-semibold whitespace-normal">
          {label}
        </TableHead>
        {columns.map(column => (
          <TableHead
            className="h-10 min-w-[120px] text-xs font-semibold"
            key={column}
          >
            {column}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((row, index) => {
        const labelValue = String(row[labelKey] ?? '');

        if (row.isSection) {
          return (
            <TableRow
              className="hover:bg-transparent"
              key={`${label}-${index.toString()}`}
            >
              <TableCell
                className="h-10 text-xs font-semibold whitespace-normal"
                colSpan={columns.length + 1}
              >
                <span
                  // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                  dangerouslySetInnerHTML={{ __html: labelValue }}
                />
              </TableCell>
            </TableRow>
          );
        }

        return (
          <TableRow
            className="hover:bg-transparent"
            key={`${label}-${index.toString()}`}
          >
            <TableCell className="h-[52px] min-w-[243px] text-sm whitespace-normal">
              <span
                // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                dangerouslySetInnerHTML={{ __html: labelValue }}
              />
            </TableCell>
            {columns.map(column => (
              <TableCell
                className="h-[52px] min-w-[120px]"
                key={`${label}-${column}-${index.toString()}`}
              >
                <span
                  // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                  dangerouslySetInnerHTML={{
                    __html: String(row[column] ?? '')
                  }}
                />
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const CalculationAccordionItem = ({
  children,
  title,
  value
}: {
  children?: React.ReactNode;
  title: string;
  value: string;
}) => (
  <Accordion.Item
    className="bg-card rounded-md border"
    value={value}
  >
    <Accordion.Header>
      <Accordion.Trigger className="group flex w-full items-center gap-5 p-5 text-left">
        <ChevronDownIcon className="size-4 transition-transform group-data-[state=open]:rotate-180" />
        <div className="text-lg font-semibold">{title}</div>
      </Accordion.Trigger>
    </Accordion.Header>
    <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden px-5 pb-5">
      <Separator />
      {children}
    </Accordion.Content>
  </Accordion.Item>
);

interface DealCalculatorProps {
  data1: GetOpportunityForRvaCalculator;
  data2: GetEquitySecurities;
  data3: GetDebtSecurities;
  data4: GetIndustries;
  data5: GetRvaCalculator;
  data6: GetSofr;
  id: string;
}

const Wrapper = () => {
  const { id, loading } = useOutletContext();
  const { dealCalculatorId } = useParams<{ dealCalculatorId: string }>();

  const { data: data1, loading: loading1 } = useQuery<
    GetOpportunityForRvaCalculator,
    { id: string }
  >(GET_OPPORTUNITY_FOR_RVA_CALCULATOR, {
    variables: { id }
  });

  const { data: data2, loading: loading2 } = useQuery<GetEquitySecurities>(
    GET_EQUITY_SECURITIES
  );

  const { data: data3, loading: loading3 } =
    useQuery<GetDebtSecurities>(GET_DEBT_SECURITIES);

  const { data: data4, loading: loading4 } =
    useQuery<GetIndustries>(GET_INDUSTRIES);

  const { data: data5, loading: loading5 } = useQuery<
    GetRvaCalculator,
    { id: string }
  >(GET_RVA_CALCULATOR, { variables: { id: dealCalculatorId ?? id } });

  const { data: data6, loading: loading6 } = useQuery<GetSofr>(GET_SOFR);

  if (
    loading ||
    loading1 ||
    loading2 ||
    loading3 ||
    loading4 ||
    loading5 ||
    loading6
  ) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1 || !data2 || !data3 || !data4 || !data5 || !data6) {
    return <div>{'Error'}</div>;
  }

  return (
    <DealCalculator
      data1={data1}
      data2={data2}
      data3={data3}
      data4={data4}
      data5={data5}
      data6={data6}
      id={id}
    />
  );
};

const DealCalculator = ({
  data1,
  data2,
  data3,
  data4,
  data5,
  data6,
  id
}: DealCalculatorProps) => {
  const matchNewCalculationPage = useMatch(
    '/investments/direct/:id/deal-calculator/new-calculation'
  );

  const [createRvaCalculator, { loading: isCreatingRvaCalculator }] =
    useMutation<{ createRvaCalculator: { calculator: { id: string } } }>(
      CREATE_RVA_CALCULATOR
    );

  const [computeFundSpecificRva, { loading: isComputingFundSpecificRva }] =
    useMutation(COMPUTE_FUND_SPECIFIC_RVA);

  const [updateConcentrationLimitLowEnd] = useMutation(
    UPDATE_CONCENTRATION_LIMIT_LOW_END
  );

  const [updateConcentrationLimitHighEnd] = useMutation(
    UPDATE_CONCENTRATION_LIMIT_HIGH_END
  );

  const [getRvaCalculatorWithConcentrationLimits] = useLazyQuery<
    GetRvaCalculatorWithConcentrationLimits,
    { id: string }
  >(GET_RVA_CALCULATOR_WITH_CONCENTRATION_LIMITS);

  const [calculationHistoryByOpId, { loading: loading1 }] = useLazyQuery<
    ListRvaCalculators,
    { opportunityId: string }
  >(LIST_RVA_CALCULATORS);

  const [getRvaCalculator] = useLazyQuery<GetRvaCalculator, { id: string }>(
    GET_RVA_CALCULATOR
  );

  const [dealLog, setDealLog] = useState<Record<string, string | undefined>>({
    'closing-fees-field': data1.node.originalIssueDiscountClosingFee
      ? data1.node.originalIssueDiscountClosingFee.toString()
      : undefined,
    'gross-unlevered-blended-irr-field': data1.node.projectedTotalIrr
      ? data1.node.projectedTotalIrr.toString()
      : undefined,
    'gross-unlevered-cash-yield-blended-field': '0',
    'gross-unlevered-debt-irr-field': data1.node.projectedDebtIrr
      ? data1.node.projectedDebtIrr.toString()
      : undefined,
    'gross-unlevered-equity-irr-field': data1.node.projectedEquityIrr
      ? data1.node.projectedEquityIrr.toString()
      : undefined,
    'pik-rate-field': '0',
    'post-fo-cash-spread-field': data1.node.postFirstOutSofrCashSpread
      ? data1.node.postFirstOutSofrCashSpread.toString()
      : undefined,
    'sofr-field': data6.currentSofrRates.rate3m
      ? (data6.currentSofrRates.rate3m * 100).toString()
      : undefined,
    'total-debt-opportunity-size-field': data1.node.smProjectedDebtInvestment
      ? data1.node.smProjectedDebtInvestment.toString()
      : undefined,
    'total-equity-opportunity-size-field': data1.node
      .smProjectedEquityInvestment
      ? data1.node.smProjectedEquityInvestment.toString()
      : undefined,
    'total-moic-gross-unlevered-field': data1.node.projectedTotalMoic
      ? data1.node.projectedTotalMoic.toString()
      : undefined
  });
  const [editDealLogDialogIsOpen, setEditDealLogDialogIsOpen] = useState(false);
  const [editPortfolioInputDialogIsOpen, setEditPortfolioInputDialogIsOpen] =
    useState(false);
  const [step, setStep] = useState(matchNewCalculationPage ? 1 : 3);

  const [fundNames, setFundNames] = useState<string[]>(() => [
    ...new Set(
      data5.rvaCalculator.calculatorFundAnalyses.map(fa => fa.fund.name)
    )
  ]);

  const [calculatorId, setCalculatorId] = useState<string>(
    data5.rvaCalculator.id
  );

  const [concentrationLimits, setConcentrationLimits] = useState(
    data5.rvaCalculator.concentrationLimits
  );

  const [portfolioInput, setPortfolioInput] = useState<
    Record<string, string | undefined>
  >(() =>
    buildPortfolioInputFromLimits(data5.rvaCalculator.concentrationLimits)
  );

  const handleBackButtonClick = useCallback(() => {
    setStep(step - 1);
  }, [step]);

  const handleDealLogFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const transactionName = dealLog['transaction-name-field'] ?? null;
    const equityPercentPlug = dealLog['equity-percent-plug-field'] ?? null;
    const debtSecurityType = dealLog['debt-security-type-field'] ?? null;
    const equitySecurityType = dealLog['equity-security-type-field'] ?? null;
    const grossUnleveredBlendedIrr =
      dealLog['gross-unlevered-blended-irr-field'] ?? null;
    const grossUnleveredCashYieldBlended =
      dealLog['gross-unlevered-cash-yield-blended-field'] ?? null;
    const grossUnleveredDebtIrr =
      dealLog['gross-unlevered-debt-irr-field'] ?? null;
    const grossUnleveredEquityIrr =
      dealLog['gross-unlevered-equity-irr-field'] ?? null;
    const industry = dealLog['industry-gics-6-field'] ?? 'Business Services';
    const industryPercentPlug = dealLog['industry-percent-plug-field'] ?? null;
    const originalIssueDiscountClosingFee =
      dealLog['closing-fees-field'] ?? null;
    const pikRate = dealLog['pik-rate-field'] ?? null;
    const postFirstOutSofrCashSpread =
      dealLog['post-fo-cash-spread-field'] ?? null;
    const projectedTotalMoic =
      dealLog['total-moic-gross-unlevered-field'] ?? null;
    const totalDebtOpportunitySize =
      dealLog['total-debt-opportunity-size-field'] ?? null;
    const totalEquityOpportunitySize =
      dealLog['total-equity-opportunity-size-field'] ?? null;

    if (!industry) {
      toast.error('Industry is missing');
      return;
    }

    if (!debtSecurityType) {
      toast.error('Debt security type is missing');
      return;
    }

    if (!equitySecurityType) {
      toast.error('Equity security type is missing');
      return;
    }

    try {
      const { data, error: error1 } = await createRvaCalculator({
        variables: {
          debtSecurityType: debtSecurityType,
          equityPercentPlug: equityPercentPlug ? +equityPercentPlug : 0,
          equitySecurityType: equitySecurityType,
          grossUnleveredBlendedIrr: grossUnleveredBlendedIrr
            ? +grossUnleveredBlendedIrr
            : 0,
          grossUnleveredCashYieldBlended: grossUnleveredCashYieldBlended
            ? +grossUnleveredCashYieldBlended
            : 0,
          grossUnleveredDebtIrr: grossUnleveredDebtIrr
            ? +grossUnleveredDebtIrr
            : 0,
          grossUnleveredEquityIrr: grossUnleveredEquityIrr
            ? +grossUnleveredEquityIrr
            : 0,
          industry,
          industryPercentPlug: industryPercentPlug ? +industryPercentPlug : 0,
          name: transactionName,
          opId: id,
          originalIssueDiscountClosingFee: originalIssueDiscountClosingFee
            ? +originalIssueDiscountClosingFee
            : 0,
          pikRate: pikRate ? +pikRate : 0,
          postFirstOutSofrCashSpread: postFirstOutSofrCashSpread
            ? +postFirstOutSofrCashSpread
            : 0,
          projectedTotalIrr: grossUnleveredBlendedIrr
            ? +grossUnleveredBlendedIrr
            : 0,
          projectedTotalMoic: projectedTotalMoic ? +projectedTotalMoic : 0,
          totalDebtOpportunitySize: totalDebtOpportunitySize
            ? +totalDebtOpportunitySize
            : 0,
          totalEquityOpportunitySize: totalEquityOpportunitySize
            ? +totalEquityOpportunitySize
            : 0,
          transactionName: transactionName
        }
      });

      if (!data) {
        throw new Error('An error has occurred');
      }

      const calcId = data.createRvaCalculator.calculator.id;

      const { error: error2 } = await computeFundSpecificRva({
        variables: { calcId }
      });

      const { data: data3, error: error3 } =
        await getRvaCalculatorWithConcentrationLimits({
          variables: { id: calcId }
        });

      if (error1 || error2 || error3 || !data3) {
        throw new Error('An error has occurred');
      }

      /* eslint-disable @typescript-eslint/no-non-null-assertion */

      const calculatorId = data3.rvaCalculator.id;
      const concentrations = data3.rvaCalculator.concentrationLimits!;

      const fundMap: Record<
        string,
        {
          calculatorId: string;
          debtConcentration: {
            actual: null | number;
            highEnd: null | number;
            id: string;
            lowEnd: null | number;
          } | null;
          equityConcentration: {
            actual: null | number;
            highEnd: null | number;
            id: string;
            lowEnd: null | number;
            testAccretiveDebtEquityMix: null | string;
            testDebtEquityDetail: null | string;
            testEquityConcentration: null | string;
          } | null;
          fundId: string;
          fundName: null | string;
          industryConcentration: {
            actual: null | number;
            highEnd: null | number;
            id: string;
            industry: null | string;
            industryCushionToHighEnd: null | number;
            lowEnd: null | number;
            testIndustryConcentrationPreAllocation: null | string;
          } | null;
        }
      > = {};

      concentrations.forEach(c => {
        const fundId = c.fund!.id;
        const fundName = c.fund!.name;

        // eslint-disable-next-line
        if (!fundMap[fundId]) {
          fundMap[fundId] = {
            calculatorId: calculatorId,
            debtConcentration: null,
            equityConcentration: null,
            fundId: fundId,
            fundName: fundName,
            industryConcentration: null
          };
        }

        if (c.limitType === 'Debt Concentration') {
          fundMap[fundId].debtConcentration = {
            actual: c.actualConcentration,
            highEnd: c.highEnd,
            id: c.id,
            lowEnd: c.lowEnd
          };
        } else if (c.limitType === 'Equity Concentration') {
          fundMap[fundId].equityConcentration = {
            actual: c.actualConcentration,
            highEnd: c.highEnd,
            id: c.id,
            lowEnd: c.lowEnd,
            testAccretiveDebtEquityMix: c.testAccretiveDebtEquityMix,
            testDebtEquityDetail: c.testDebtEquityDetail,
            testEquityConcentration: c.testEquityConcentration
          };
        } else if (c.limitType === 'Industry Concentration') {
          fundMap[fundId].industryConcentration = {
            actual: c.actualConcentration,
            highEnd: c.highEnd,
            id: c.id,
            industry: c.industry,
            industryCushionToHighEnd: c.industryCushionToHighEnd,
            lowEnd: c.lowEnd,
            testIndustryConcentrationPreAllocation:
              c.testIndustryConcentrationPreAllocation
          };
        }
      });

      /* eslint-enable @typescript-eslint/no-non-null-assertion */

      // Derive fund names and portfolio input defaults from concentration limits
      const newFundNames = [...new Set(concentrations.map(c => c.fund?.name))];
      setCalculatorId(calcId);
      setConcentrationLimits(
        concentrations as unknown as typeof data5.rvaCalculator.concentrationLimits
      );
      setFundNames(newFundNames);
      setPortfolioInput(buildPortfolioInputFromLimits(concentrations));

      setStep(2);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const [dataTable1, setDataTable1] = useState<DataTableRow[]>([]);
  const [dataTable2, setDataTable2] = useState<DataTableRow[]>([]);
  const [dataTable3, setDataTable3] = useState<DataTableRow[]>([]);
  const [dataTable4, setDataTable4] = useState<DataTableRow[]>([]);
  const [dataTable5, setDataTable5] = useState<DataTableRow[]>([]);
  const [dataTable6, setDataTable6] = useState<DataTableRow[]>([]);
  const [dataTable7, setDataTable7] = useState<DataTableRow[]>([]);
  const [dataTable8, setDataTable8] = useState<DataTableRow[]>([]);

  const getTableData = useCallback(async () => {
    const { data } = await getRvaCalculator({
      variables: { id: calculatorId }
    });

    if (!data) {
      throw new Error();
    }

    const calculator = data.rvaCalculator;

    const getFundAnalysis = (fundName: string) => {
      return calculator.calculatorFundAnalyses.find(
        fa => fa.fund.name === fundName
      );
    };

    const getConcentrationLimit = (fundName: string, limitType: string) => {
      return calculator.concentrationLimits.find(
        cl => cl.fund.name === fundName && cl.limitType === limitType
      );
    };

    const fundNames = [
      ...new Set(calculator.calculatorFundAnalyses.map(fa => fa.fund.name))
    ];

    const createPill = (value?: null | string) => {
      if (!value || value === 'n/a') return '';

      let bgColor, textColor;

      if (value === 'Clear') {
        bgColor = '#BBF7D0';
        textColor = '#166534';
      } else if (value === 'Consider') {
        bgColor = '#FEF08A';
        textColor = '#A16207';
      } else if (value === 'Fail') {
        bgColor = '#FECACA';
        textColor = '#B91C1C';
      } else {
        if (value === 'Yes') {
          bgColor = '#BBF7D0';
          textColor = '#166534';
        } else {
          bgColor = '#FECACA';
          textColor = '#B91C1C';
        }
      }

      return `<span style="background-color: ${bgColor}; color: ${textColor}; padding: 4px 12px; border-radius: 12px; font-weight: 500; display: inline-block;">${value}</span>`;
    };

    const formatPercent = (value?: null | number) => {
      if (value == null || Number.isNaN(value)) return '';
      const normalized = value > 1 ? value / 100 : value;
      const percentValue = normalized * 100;

      // Determine precision: 3 decimals if needed, otherwise 2
      const str = percentValue.toFixed(3);
      const decimals = str.split('.')[1] || '';
      const precision = decimals[2] && decimals[2] !== '0' ? 3 : 2;

      // Only show decimals if they exist
      const hasDecimal = percentValue % 1 !== 0;
      const formatted = hasDecimal
        ? percentValue.toFixed(precision)
        : Math.round(percentValue).toString();

      return '<i>' + formatted + '%</i>';
    };

    const formatNumber = (value?: null | number) => {
      if (value == null || Number.isNaN(value)) return '';
      const hasDecimal = value % 1 !== 0;
      return hasDecimal ? value.toFixed(2) : value.toString();
    };

    const createRelativeValuePill = (value?: null | string) => {
      if (!value || value === 'n/a') return '';

      const match = /^(\d+)\/(\d+)$/.exec(value);
      if (!match) return value;

      const numerator = parseInt(match[1]);
      const denominator = parseInt(match[2]);
      const ratio = denominator > 0 ? numerator / denominator : 0;

      let bgColor, textColor;

      if (ratio < 0.5) {
        bgColor = '#FECACA';
        textColor = '#B91C1C';
      } else if (ratio === 0.5) {
        return value;
      } else {
        bgColor = '#BBF7D0';
        textColor = '#166534';
      }

      return `<span style="background-color: ${bgColor}; color: ${textColor}; padding: 4px 12px; border-radius: 12px; font-weight: 500; display: inline-block;">${value}</span>`;
    };

    const createRow = (
      labelKey: string,
      label: string,
      isSection = false
    ): DataTableRow => ({
      isSection,
      [labelKey]: label
    });

    const getTableData1 = () => {
      const rows: DataTableRow[] = [];

      const passFailRow = createRow('Assumptions', 'Global Pass/Fail');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );

        const allPass =
          fa?.testAboveFundExpenses === 'Clear' &&
          fa.testNetIncomeGeneration === 'Clear' &&
          fa.testAboveFundHurdleRate === 'Clear' &&
          industryConc?.testIndustryConcentrationPreAllocation === 'Clear' &&
          equityConc?.testEquityConcentration === 'Clear';

        const result = allPass ? 'Clear' : 'Fail';
        passFailRow[fundName] = createPill(result);
      });

      rows.push(passFailRow);

      const accretiveValueSection = createRow(
        'Assumptions',
        '<strong>1. Accretive Value</strong>',
        true
      );
      fundNames.forEach(fundName => {
        accretiveValueSection[fundName] = '';
      });
      rows.push(accretiveValueSection);

      const netIncomeRow = createRow('Assumptions', 'Net Income Generation');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const result = fa?.testNetIncomeGeneration ?? '';
        netIncomeRow[fundName] = createPill(result);
      });

      rows.push(netIncomeRow);

      const portfolioConcentrationSection = createRow(
        'Assumptions',
        '<strong>2. Portfolio Concentration</strong>',
        true
      );
      fundNames.forEach(fundName => {
        portfolioConcentrationSection[fundName] = '';
      });
      rows.push(portfolioConcentrationSection);

      const industryConcentrationRow = createRow(
        'Assumptions',
        'Industry Concentration Pre-Allocation'
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const result =
          industryConc?.testIndustryConcentrationPreAllocation ?? '';
        industryConcentrationRow[fundName] = createPill(result);
      });

      rows.push(industryConcentrationRow);

      const equityConcentrationRow = createRow(
        'Assumptions',
        'Current Concentration Equity'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const result = equityConc?.testEquityConcentration ?? '';
        equityConcentrationRow[fundName] = createPill(result);
      });

      rows.push(equityConcentrationRow);

      return rows;
    };

    const getTableData2 = () => {
      const rows: DataTableRow[] = [];

      const accretiveValueSection = createRow(
        'Additional Considerations',
        '<strong>1. Accretive Value</strong>',
        true
      );
      fundNames.forEach(fundName => {
        accretiveValueSection[fundName] = '';
      });
      rows.push(accretiveValueSection);

      const hurdleRateRow = createRow(
        'Additional Considerations',
        "Above Fund's Hurdle Rate?"
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const result = fa?.testAboveFundHurdleRate ?? '';
        hurdleRateRow[fundName] = createPill(result);
      });

      rows.push(hurdleRateRow);

      const portfolioConcentrationSection = createRow(
        'Additional Considerations',
        '<strong>2. Portfolio Concentration</strong>',
        true
      );
      fundNames.forEach(fundName => {
        portfolioConcentrationSection[fundName] = '';
      });
      rows.push(portfolioConcentrationSection);

      const industryCushionRow = createRow(
        'Additional Considerations',
        'Industry Cushion to High End %'
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const cushion = industryConc?.industryCushionToHighEnd;
        industryCushionRow[fundName] = formatPercent(cushion);
      });

      rows.push(industryCushionRow);

      const debtEquityDetailRow = createRow(
        'Additional Considerations',
        'Debt / Equity Detail'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const detail = equityConc?.testDebtEquityDetail ?? '';
        debtEquityDetailRow[fundName] =
          detail === 'n/a' ? 'N/A' : createPill(detail);
      });

      rows.push(debtEquityDetailRow);

      const accretiveMixRow = createRow(
        'Additional Considerations',
        'Accretive to Debt / Equity Mix'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const result = equityConc?.testAccretiveDebtEquityMix ?? '';
        accretiveMixRow[fundName] = createPill(result);
      });

      rows.push(accretiveMixRow);

      const relativeValueSection = createRow(
        'Additional Considerations',
        '<strong>3. Relative Value - Average of Unmandated Deals</strong>',
        true
      );
      fundNames.forEach(fundName => {
        relativeValueSection[fundName] = '';
      });
      rows.push(relativeValueSection);

      const grossUnleveredIrrRow = createRow(
        'Additional Considerations',
        'Total IRR (Gross Unlevered)'
      );

      fundNames.forEach(fundName => {
        grossUnleveredIrrRow[fundName] = formatPercent(
          calculator.grossUnleveredBlendedIrr
        );
      });

      rows.push(grossUnleveredIrrRow);

      const testGrossUnleveredRow = createRow(
        'Additional Considerations',
        'Test: Total IRR Gross Unlevered'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const result = fa?.testTotalIrrGrossUnlevered ?? '';
        testGrossUnleveredRow[fundName] = createPill(result);
      });

      rows.push(testGrossUnleveredRow);

      const netLeveredIrrRow = createRow(
        'Additional Considerations',
        'Total IRR (Net Levered)'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const netIrr = fa?.netIrrLevered;
        netLeveredIrrRow[fundName] = formatPercent(netIrr);
      });

      rows.push(netLeveredIrrRow);

      const testNetLeveredRow = createRow(
        'Additional Considerations',
        'Test: Total IRR Net Levered'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const result = fa?.testTotalIrrNetLevered ?? '';
        testNetLeveredRow[fundName] = createPill(result);
      });

      rows.push(testNetLeveredRow);

      return rows;
    };

    const getTableData3 = () => {
      const rows: DataTableRow[] = [];

      const managementFeesRow = createRow(
        'Fund Assumptions',
        'Management Fees'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const managementFees = fa?.fund.managementFees;
        managementFeesRow[fundName] = formatPercent(managementFees);
      });

      rows.push(managementFeesRow);

      const fundExpensesRow = createRow('Fund Assumptions', 'Fund Expenses');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const adminExpenses = fa?.fund.adminExpenses;
        const adjustedExpenses =
          adminExpenses != null ? adminExpenses / 100 : null;
        fundExpensesRow[fundName] = formatPercent(adjustedExpenses);
      });

      rows.push(fundExpensesRow);

      const carriedInterestRow = createRow(
        'Fund Assumptions',
        'Carried Interest'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const carriedInterest = fa?.fund.carriedInterest;
        carriedInterestRow[fundName] = formatPercent(carriedInterest);
      });

      rows.push(carriedInterestRow);

      const hurdleRateRow = createRow('Fund Assumptions', 'Hurdle Rate');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const hurdleRate = fa?.fund.hurdleRate;
        hurdleRateRow[fundName] = formatPercent(hurdleRate);
      });

      rows.push(hurdleRateRow);

      const debtAssetRatioRow = createRow(
        'Fund Assumptions',
        'Debt / Asset Ratio'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const leverageDebtToTotalAssets = fa?.fund.leverageDebtToTotalAssets;
        debtAssetRatioRow[fundName] = formatPercent(leverageDebtToTotalAssets);
      });

      rows.push(debtAssetRatioRow);

      const leverageSpreadRow = createRow(
        'Fund Assumptions',
        'Leverage Spread'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const ablSpread = fa?.fund.ablSpread;
        leverageSpreadRow[fundName] = formatPercent(ablSpread);
      });

      rows.push(leverageSpreadRow);

      const totalCostOfLeverageRow = createRow(
        'Fund Assumptions',
        'Total Cost of Leverage'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const leverageCost = fa?.fund.leverageCost;
        totalCostOfLeverageRow[fundName] = formatPercent(leverageCost);
      });

      rows.push(totalCostOfLeverageRow);

      const newBlendedIrrRow = createRow('Fund Assumptions', 'New Blended IRR');

      fundNames.forEach(fundName => {
        const blendedIrr = calculator.grossUnleveredBlendedIrr;
        newBlendedIrrRow[fundName] = formatPercent(blendedIrr);
      });

      rows.push(newBlendedIrrRow);

      return rows;
    };

    const getTableData4 = () => {
      const rows: DataTableRow[] = [];

      const managementFeesRow = createRow(
        'Step 1: Asset Yield',
        'Management Fees'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const managementFees = fa?.fund.managementFees;
        managementFeesRow[fundName] = formatPercent(managementFees);
      });

      rows.push(managementFeesRow);

      const fundExpensesRow = createRow('Step 1: Asset Yield', 'Fund Expenses');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const adminExpenses = fa?.fund.adminExpenses;
        const adjustedExpenses =
          adminExpenses != null ? adminExpenses / 100 : null;
        fundExpensesRow[fundName] = formatPercent(adjustedExpenses);
      });

      rows.push(fundExpensesRow);

      const netAssetYieldRow = createRow(
        'Step 1: Asset Yield',
        '<strong>Net Asset Yield Pre-Hurdle</strong>'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const netAssetYield = fa?.netAssetYieldPreHurdle;
        netAssetYieldRow[fundName] =
          '<strong>' + formatPercent(netAssetYield) + '</strong>';
      });

      rows.push(netAssetYieldRow);

      const testAboveFundExpensesRow = createRow(
        'Step 1: Asset Yield',
        '<strong>Test: Above Fund Expenses?</strong>'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testAboveFundExpenses ?? '';
        testAboveFundExpensesRow[fundName] = createPill(testResult);
      });

      rows.push(testAboveFundExpensesRow);

      return rows;
    };

    const getTableData5 = () => {
      const rows: DataTableRow[] = [];

      const netAssetYieldRow = createRow(
        'Step 2: Net Income Generation',
        'Net Asset Yield Pre-Hurdle'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const netAssetYield = fa?.netAssetYieldPreHurdle;
        netAssetYieldRow[fundName] = formatPercent(netAssetYield);
      });

      rows.push(netAssetYieldRow);

      const debtEquityRatioRow = createRow(
        'Step 2: Net Income Generation',
        'Debt / Equity Ratio'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const debtToEquityRatio = fa?.debtToEquityRatio;
        debtEquityRatioRow[fundName] = formatNumber(debtToEquityRatio);
      });

      rows.push(debtEquityRatioRow);

      const benefitsOfLeverageRow = createRow(
        'Step 2: Net Income Generation',
        'Benefits of Leverage'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const benefitsOfLeverage = fa?.benefitsOfLeverage;
        benefitsOfLeverageRow[fundName] = formatPercent(benefitsOfLeverage);
      });

      rows.push(benefitsOfLeverageRow);

      const costsOfLeverageRow = createRow(
        'Step 2: Net Income Generation',
        'Costs of Leverage'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const costsOfLeverage = fa?.costsOfLeverage;
        costsOfLeverageRow[fundName] = formatPercent(costsOfLeverage);
      });

      rows.push(costsOfLeverageRow);

      const netYieldPreHurdleRow = createRow(
        'Step 2: Net Income Generation',
        '<strong>Net Yield Pre-Hurdle</strong>'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const netYieldPreHurdle = fa?.netYieldPreHurdle;
        netYieldPreHurdleRow[fundName] =
          '<strong>' + formatPercent(netYieldPreHurdle) + '</strong>';
      });

      rows.push(netYieldPreHurdleRow);

      const testNetIncomeRow = createRow(
        'Step 2: Net Income Generation',
        '<strong>Test: Net Income Generation</strong>'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testNetIncomeGeneration ?? '';
        testNetIncomeRow[fundName] = createPill(testResult);
      });

      rows.push(testNetIncomeRow);

      return rows;
    };

    const getTableData6 = () => {
      const rows: DataTableRow[] = [];

      const netYieldPreHurdleRow = createRow(
        'Step 3: Carry',
        '<strong>Net Yield Pre-Hurdle</strong>'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const netYieldPreHurdle = fa?.netYieldPreHurdle;
        netYieldPreHurdleRow[fundName] =
          '<strong>' + formatPercent(netYieldPreHurdle) + '</strong>';
      });

      rows.push(netYieldPreHurdleRow);

      const carriedInterestRow = createRow('Step 3: Carry', 'Carried Interest');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const carriedInterest = fa?.fund.carriedInterest;
        carriedInterestRow[fundName] = formatPercent(carriedInterest);
      });

      rows.push(carriedInterestRow);

      const hurdleRateRow = createRow('Step 3: Carry', 'Hurdle Rate');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const hurdleRate = fa?.fund.hurdleRate;
        hurdleRateRow[fundName] = formatPercent(hurdleRate);
      });

      rows.push(hurdleRateRow);

      const carryPaidRow = createRow('Step 3: Carry', 'Carry Paid');

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const carryPaid = fa?.carryPaid;
        carryPaidRow[fundName] = formatPercent(carryPaid);
      });

      rows.push(carryPaidRow);

      const leveredYieldPostCarryRow = createRow(
        'Step 3: Carry',
        '<strong>Levered Yield Post-Carry</strong>'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const leveredYieldPostCarry = fa?.leveredYieldPostCarry;
        leveredYieldPostCarryRow[fundName] =
          '<strong>' + formatPercent(leveredYieldPostCarry) + '</strong>';
      });

      rows.push(leveredYieldPostCarryRow);

      const testAboveHurdleRow = createRow(
        'Step 3: Carry',
        "<b>Test: Above Fund's Hurdle Rate?</b>"
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testAboveFundHurdleRate ?? '';
        testAboveHurdleRow[fundName] = createPill(testResult);
      });

      rows.push(testAboveHurdleRow);

      return rows;
    };

    const getTableData7 = () => {
      const industry = calculator.industry ?? 'Industry';
      const rows: DataTableRow[] = [];

      const industryCurrentConcentrationRow = createRow(
        'Concentration Test',
        `<strong>${industry} - Current Concentration</strong>`
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const actualConcentration = industryConc?.actualConcentration;
        industryCurrentConcentrationRow[fundName] =
          '<strong>' + formatPercent(actualConcentration) + '</strong>';
      });

      rows.push(industryCurrentConcentrationRow);

      const industryLowEndRow = createRow(
        'Concentration Test',
        'Range - Low End'
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const lowEnd = industryConc?.lowEnd;
        industryLowEndRow[fundName] = formatPercent(lowEnd);
      });

      rows.push(industryLowEndRow);

      const industryHighEndRow = createRow(
        'Concentration Test',
        'Range - High End'
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const highEnd = industryConc?.highEnd;
        industryHighEndRow[fundName] = formatPercent(highEnd);
      });

      rows.push(industryHighEndRow);

      const industryCushionRow = createRow(
        'Concentration Test',
        '<strong>Industry Cushion to High End</strong>'
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const cushion = industryConc?.industryCushionToHighEnd;
        industryCushionRow[fundName] =
          '<strong>' + formatPercent(cushion) + '</strong>';
      });

      rows.push(industryCushionRow);

      const testIndustryConcentrationRow = createRow(
        'Concentration Test',
        '<strong>Test: Industry Concentration - Pre-Allocation</strong>'
      );

      fundNames.forEach(fundName => {
        const industryConc = getConcentrationLimit(
          fundName,
          'Industry Concentration'
        );
        const testResult =
          industryConc?.testIndustryConcentrationPreAllocation ?? '';
        testIndustryConcentrationRow[fundName] = createPill(testResult);
      });

      rows.push(testIndustryConcentrationRow);

      const debtCurrentConcentrationRow = createRow(
        'Concentration Test',
        '<strong>Debt - Current Concentration</strong>'
      );

      fundNames.forEach(fundName => {
        const debtConc = getConcentrationLimit(fundName, 'Debt Concentration');
        const actualConcentration = debtConc?.actualConcentration;
        debtCurrentConcentrationRow[fundName] =
          '<strong>' + formatPercent(actualConcentration) + '</strong>';
      });

      rows.push(debtCurrentConcentrationRow);

      const debtLowEndRow = createRow('Concentration Test', 'Range - Low End');

      fundNames.forEach(fundName => {
        const debtConc = getConcentrationLimit(fundName, 'Debt Concentration');
        const lowEnd = debtConc?.lowEnd;
        debtLowEndRow[fundName] = formatPercent(lowEnd);
      });

      rows.push(debtLowEndRow);

      const debtHighEndRow = createRow(
        'Concentration Test',
        'Range - High End'
      );

      fundNames.forEach(fundName => {
        const debtConc = getConcentrationLimit(fundName, 'Debt Concentration');
        const highEnd = debtConc?.highEnd;
        debtHighEndRow[fundName] = formatPercent(highEnd);
      });

      rows.push(debtHighEndRow);

      const equityCurrentConcentrationRow = createRow(
        'Concentration Test',
        '<strong>Equity - Current Concentration</strong>'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const actualConcentration = equityConc?.actualConcentration;
        equityCurrentConcentrationRow[fundName] =
          '<strong>' + formatPercent(actualConcentration) + '</strong>';
      });

      rows.push(equityCurrentConcentrationRow);

      const equityLowEndRow = createRow(
        'Concentration Test',
        'Range - Low End'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const lowEnd = equityConc?.lowEnd;
        equityLowEndRow[fundName] = formatPercent(lowEnd);
      });

      rows.push(equityLowEndRow);

      const equityHighEndRow = createRow(
        'Concentration Test',
        'Range - High End'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const highEnd = equityConc?.highEnd;
        equityHighEndRow[fundName] = formatPercent(highEnd);
      });

      rows.push(equityHighEndRow);

      const flagEquityConcentrationRow = createRow(
        'Concentration Test',
        '<strong>Flag: Current Concentration - Equity</strong>'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const testResult = equityConc?.testEquityConcentration ?? '';
        flagEquityConcentrationRow[fundName] = createPill(testResult);
      });

      rows.push(flagEquityConcentrationRow);

      const testAccretiveMixRow = createRow(
        'Concentration Test',
        '<strong>Test: Accretive to Debt / Equity Mix</strong>'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const testResult = equityConc?.testAccretiveDebtEquityMix ?? '';
        testAccretiveMixRow[fundName] = createPill(testResult);
      });

      rows.push(testAccretiveMixRow);

      const debtEquityDetailRow = createRow(
        'Concentration Test',
        '<strong>Debt / Equity Detail</strong>'
      );

      fundNames.forEach(fundName => {
        const equityConc = getConcentrationLimit(
          fundName,
          'Equity Concentration'
        );
        const detail = equityConc?.testDebtEquityDetail ?? '';
        debtEquityDetailRow[fundName] =
          detail === 'n/a' ? 'N/A' : createPill(detail);
      });

      rows.push(debtEquityDetailRow);

      return rows;
    };

    const getTableData8 = () => {
      const rows: DataTableRow[] = [];

      const postFoSofrCashSpreadRow = createRow(
        'Relative Value Analysis',
        'Post-FO SOFR Cash Spread'
      );

      fundNames.forEach(fundName => {
        const postFoSofrCashSpread = calculator.postFirstOutSofrCashSpread;
        postFoSofrCashSpreadRow[fundName] = formatPercent(postFoSofrCashSpread);
      });

      rows.push(postFoSofrCashSpreadRow);

      const postFoCashInterestRateRow = createRow(
        'Relative Value Analysis',
        'Post-FO Cash Interest Rate'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const postFoCashInterestRate = fa?.postFirstOutCashInterestRate;
        postFoCashInterestRateRow[fundName] = formatPercent(
          postFoCashInterestRate
        );
      });

      rows.push(postFoCashInterestRateRow);

      const totalIrrGrossUnleveredRow = createRow(
        'Relative Value Analysis',
        'Total IRR (Gross Unlevered)'
      );

      fundNames.forEach(fundName => {
        const grossUnleveredBlendedIrr = calculator.grossUnleveredBlendedIrr;
        totalIrrGrossUnleveredRow[fundName] = formatPercent(
          grossUnleveredBlendedIrr
        );
      });

      rows.push(totalIrrGrossUnleveredRow);

      const totalMoicGrossUnleveredRow = createRow(
        'Relative Value Analysis',
        'Total MOIC (Gross Unlevered)'
      );

      fundNames.forEach(fundName => {
        const projectedTotalMoic = calculator.projectedTotalMoic;
        totalMoicGrossUnleveredRow[fundName] = formatNumber(projectedTotalMoic);
      });

      rows.push(totalMoicGrossUnleveredRow);

      const debtPercentOfTotalCapitalRow = createRow(
        'Relative Value Analysis',
        'Debt % of Total Capital'
      );

      fundNames.forEach(fundName => {
        const debtPercent = calculator.debtPercentOfInvestedCapital;
        debtPercentOfTotalCapitalRow[fundName] = formatPercent(debtPercent);
      });

      rows.push(debtPercentOfTotalCapitalRow);

      const effectiveUnleveredCashYieldOnDebtRow = createRow(
        'Relative Value Analysis',
        'Effective Unlevered Cash Yield on Debt'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const effectiveYield = fa?.effectiveUnleveredCashYieldOnDebt;
        effectiveUnleveredCashYieldOnDebtRow[fundName] =
          formatPercent(effectiveYield);
      });

      rows.push(effectiveUnleveredCashYieldOnDebtRow);

      const effectiveUnleveredCashYieldOnTotalCapitalRow = createRow(
        'Relative Value Analysis',
        'Effective Unlevered Cash Yield on Total Capital'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const effectiveYield = fa?.effectiveUnleveredCashYieldOnTotalCapital;
        effectiveUnleveredCashYieldOnTotalCapitalRow[fundName] =
          formatPercent(effectiveYield);
      });

      rows.push(effectiveUnleveredCashYieldOnTotalCapitalRow);

      const avgPostFoSofrCashSpreadRow = createRow(
        'Relative Value Analysis',
        'Post-FO SOFR Cash Spread (Averages)'
      );

      fundNames.forEach(fundName => {
        const postFoSofrCashSpread = calculator.postFirstOutSofrCashSpread;
        avgPostFoSofrCashSpreadRow[fundName] =
          formatPercent(postFoSofrCashSpread);
      });

      rows.push(avgPostFoSofrCashSpreadRow);

      const avgPostFoCashInterestRateRow = createRow(
        'Relative Value Analysis',
        'Post-FO Cash Interest Rate (Averages)'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const postFoCashInterestRate = fa?.postFirstOutCashInterestRate;
        avgPostFoCashInterestRateRow[fundName] = formatPercent(
          postFoCashInterestRate
        );
      });

      rows.push(avgPostFoCashInterestRateRow);

      const avgEffectiveLeveredCashYieldRow = createRow(
        'Relative Value Analysis',
        'Effective Levered Cash Yield on Total Capital (Averages)'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const effectiveYield = fa?.effectiveLeveredCashYieldOnTotalCapital;
        avgEffectiveLeveredCashYieldRow[fundName] =
          formatPercent(effectiveYield);
      });

      rows.push(avgEffectiveLeveredCashYieldRow);

      const avgTotalIrrGrossUnleveredRow = createRow(
        'Relative Value Analysis',
        'Total IRR (Gross Unlevered) (Averages)'
      );

      fundNames.forEach(fundName => {
        const grossUnleveredBlendedIrr = calculator.grossUnleveredBlendedIrr;
        avgTotalIrrGrossUnleveredRow[fundName] = formatPercent(
          grossUnleveredBlendedIrr
        );
      });

      rows.push(avgTotalIrrGrossUnleveredRow);

      const avgTotalIrrNetLeveredRow = createRow(
        'Relative Value Analysis',
        'Total IRR (Net Levered) (Averages)'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const netIrrLevered = fa?.netIrrLevered;
        avgTotalIrrNetLeveredRow[fundName] = formatPercent(netIrrLevered);
      });

      rows.push(avgTotalIrrNetLeveredRow);

      const testPostFoSofrCashSpreadRow = createRow(
        'Relative Value Analysis',
        'Post-FO SOFR Cash Spread - Clear / Fail'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testPostFoSofrCashSpread ?? '';
        testPostFoSofrCashSpreadRow[fundName] = createPill(testResult);
      });

      rows.push(testPostFoSofrCashSpreadRow);

      const testPostFoCashInterestRateRow = createRow(
        'Relative Value Analysis',
        'Post-FO Cash Interest Rate - Clear / Fail'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testPostFoCashInterestRate ?? '';
        testPostFoCashInterestRateRow[fundName] = createPill(testResult);
      });

      rows.push(testPostFoCashInterestRateRow);

      const testEffectiveLeveredCashYieldRow = createRow(
        'Relative Value Analysis',
        'Effective Levered Cash Yield on Total Capital - Clear / Fail'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testEffectiveLeveredCashYield ?? '';
        testEffectiveLeveredCashYieldRow[fundName] = createPill(testResult);
      });

      rows.push(testEffectiveLeveredCashYieldRow);

      const testTotalIrrGrossUnleveredRow = createRow(
        'Relative Value Analysis',
        'Total IRR (Gross Unlevered) - Clear / Fail'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testTotalIrrGrossUnlevered ?? '';
        testTotalIrrGrossUnleveredRow[fundName] = createPill(testResult);
      });

      rows.push(testTotalIrrGrossUnleveredRow);

      const testTotalIrrNetLeveredRow = createRow(
        'Relative Value Analysis',
        'Total IRR (Net Levered) - Clear / Fail'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testResult = fa?.testTotalIrrNetLevered ?? '';
        testTotalIrrNetLeveredRow[fundName] = createPill(testResult);
      });

      rows.push(testTotalIrrNetLeveredRow);

      const relativeValueRow = createRow(
        'Relative Value Analysis',
        'Relative Value - Clear / Fail'
      );

      fundNames.forEach(fundName => {
        const fa = getFundAnalysis(fundName);
        const testRelativeValue = fa?.testRelativeValue ?? '';
        relativeValueRow[fundName] = createRelativeValuePill(testRelativeValue);
      });

      rows.push(relativeValueRow);

      return rows;
    };

    const t1 = getTableData1();
    const t2 = getTableData2();
    const t3 = getTableData3();
    const t4 = getTableData4();
    const t5 = getTableData5();
    const t6 = getTableData6();
    const t7 = getTableData7();
    const t8 = getTableData8();
    setDataTable1(t1);
    setDataTable2(t2);
    setDataTable3(t3);
    setDataTable4(t4);
    setDataTable5(t5);
    setDataTable6(t6);
    setDataTable7(t7);
    setDataTable8(t8);
  }, [calculatorId, getRvaCalculator]);

  const handlePortfolioInputFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    try {
      const { error } = await calculationHistoryByOpId({
        variables: { opportunityId: id }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      await getTableData();

      setStep(3);
    } catch (error_) {
      const error = error_ as Error;
      toast.error(error.message);
    }
  };

  useEffect(() => {
    void (async () => {
      if (!matchNewCalculationPage) {
        await getTableData();
      }
    })();
  }, [getTableData, matchNewCalculationPage]);

  useEffect(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, [step]);

  const handleEditDealLogFormSubmit = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const newDealLog: Record<string, string | undefined> = {};
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        newDealLog[key] = value === '' ? undefined : value;
      }
    });

    setDealLog(newDealLog);

    setEditDealLogDialogIsOpen(false);
  };

  const handleEditPortfolioInputFormSubmit = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const newPortfolioInput: Record<string, string | undefined> = {};
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        newPortfolioInput[key] = value === '' ? undefined : value;
      }
    });

    setPortfolioInput(newPortfolioInput);

    setEditPortfolioInputDialogIsOpen(false);
  };

  const handleConcentrationLimitBlur = async (
    event: React.FocusEvent<HTMLInputElement>,
    isHighEnd: boolean
  ) => {
    const fieldName = event.currentTarget.name;

    if (
      !fieldName.includes('range-low-end-field') &&
      !fieldName.includes('range-high-end-field')
    ) {
      return;
    }

    const numericValue = Number(event.currentTarget.value);

    if (Number.isNaN(numericValue)) {
      return;
    }

    const normalizedFieldPrefix = fieldName.split('-current-concentration')[0];
    const limitType = fieldName.includes('debt-current-concentration')
      ? 'Debt Concentration'
      : fieldName.includes('equity-current-concentration')
        ? 'Equity Concentration'
        : 'Industry Concentration';

    const matchingLimit = concentrationLimits.find(limit => {
      const normalizedFundName = limit.fund.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return (
        normalizedFieldPrefix === normalizedFundName &&
        limit.limitType === limitType
      );
    });

    if (!matchingLimit) {
      return;
    }

    try {
      if (isHighEnd) {
        await updateConcentrationLimitHighEnd({
          variables: {
            calculatorId,
            fundId: matchingLimit.fund.id,
            highEnd: numericValue,
            id: matchingLimit.id,
            limitType: matchingLimit.limitType
          }
        });
        return;
      }

      await updateConcentrationLimitLowEnd({
        variables: {
          calculatorId,
          fundId: matchingLimit.fund.id,
          id: matchingLimit.id,
          limitType: matchingLimit.limitType,
          lowEnd: numericValue
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleConcentrationLimitLowEndBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    await handleConcentrationLimitBlur(event, false);
  };

  const handleConcentrationLimitHighEndBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    await handleConcentrationLimitBlur(event, true);
  };

  const totalDebtOpportunitySizeValue = Number(
    dealLog['total-debt-opportunity-size-field'] ?? 0
  );
  const totalEquityOpportunitySizeValue = Number(
    dealLog['total-equity-opportunity-size-field'] ?? 0
  );
  const equitySecurityTypeValue = dealLog['equity-security-type-field'];
  const equitySecurityTypeLabel = equitySecurityTypeValue
    ? (data2.equitySecurities.find(
        security => security.value === equitySecurityTypeValue
      )?.name ?? equitySecurityTypeValue)
    : undefined;
  const debtSecurityTypeValue = dealLog['debt-security-type-field'];
  const debtSecurityTypeLabel = debtSecurityTypeValue
    ? (data3.debtSecurities.find(
        security => security.value === debtSecurityTypeValue
      )?.name ?? debtSecurityTypeValue)
    : undefined;
  const industryValue = dealLog['industry-gics-6-field'];
  const industryLabel = industryValue
    ? (data4.rvaCalculatorIndustries.find(
        industry => industry.value === industryValue
      )?.label ?? industryValue)
    : undefined;
  const totalOpportunityValue =
    totalDebtOpportunitySizeValue + totalEquityOpportunitySizeValue;
  const totalOpportunitySize = totalOpportunityValue
    ? totalOpportunityValue.toString()
    : undefined;
  const debtPercentOfInvestedCapital = totalOpportunityValue
    ? ((totalDebtOpportunitySizeValue / totalOpportunityValue) * 100).toFixed(2)
    : undefined;
  const equityPercentOfInvestedCapital = totalOpportunityValue
    ? ((totalEquityOpportunitySizeValue / totalOpportunityValue) * 100).toFixed(
        2
      )
    : undefined;

  return (
    <>
      <Header />
      <div className="grid grid-cols-[1fr_auto]">
        <div className="min-w-0 space-y-7 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="bg-card flex justify-center rounded-[10px] border pt-4 pb-1.5">
            <div className="flex w-27.5 flex-col items-center gap-y-2">
              {step === 1 ? (
                <div className="bg-dark-blue-primary-smc-200 relative size-6 rounded-full">
                  <div className="bg-dark-blue-primary-smc-500 absolute inset-0 m-auto size-5 rounded-full">
                    <div className="absolute inset-0 m-auto size-2.5 rounded-full bg-white" />
                  </div>
                </div>
              ) : (
                <div className="bg-dark-blue-primary-smc-200 relative size-6 rounded-full">
                  <div className="bg-dark-blue-primary-smc-500 absolute inset-0 m-auto size-5 rounded-full">
                    <CheckIcon
                      className="absolute inset-0 m-auto text-white"
                      size={10}
                    />
                  </div>
                </div>
              )}
              <div className="text-center text-sm">Deal Log</div>
            </div>
            <div className="bg-border -mx-10.75 mt-3 h-0.25 w-54.5" />
            <div className="flex w-27.5 flex-col items-center gap-y-2">
              {step < 2 ? (
                <div className="size-6 rounded-full border-2" />
              ) : step === 2 ? (
                <div className="bg-dark-blue-primary-smc-200 relative size-6 rounded-full">
                  <div className="bg-dark-blue-primary-smc-500 absolute inset-0 m-auto size-5 rounded-full">
                    <div className="absolute inset-0 m-auto size-2.5 rounded-full bg-white" />
                  </div>
                </div>
              ) : (
                <div className="bg-dark-blue-primary-smc-200 relative size-6 rounded-full">
                  <div className="bg-dark-blue-primary-smc-500 absolute inset-0 m-auto size-5 rounded-full">
                    <CheckIcon
                      className="absolute inset-0 m-auto text-white"
                      size={10}
                    />
                  </div>
                </div>
              )}
              <div className="text-center text-sm">Portfolio Inputs</div>
            </div>
            <div className="bg-border -mx-10.75 mt-3 h-0.25 w-54.5" />
            <div className="flex w-27.5 flex-col items-center gap-y-2">
              {step === 3 ? (
                <div className="bg-dark-blue-primary-smc-200 relative size-6 rounded-full">
                  <div className="bg-dark-blue-primary-smc-500 absolute inset-0 m-auto size-5 rounded-full">
                    <div className="absolute inset-0 m-auto size-2.5 rounded-full bg-white" />
                  </div>
                </div>
              ) : (
                <div className="size-6 rounded-full border-2" />
              )}
              <div className="text-center text-sm">Calculations</div>
            </div>
          </div>
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">Deal Log</div>
                <Dialog
                  onOpenChange={setEditDealLogDialogIsOpen}
                  open={editDealLogDialogIsOpen}
                >
                  <DialogTrigger asChild>
                    <EditButton />
                  </DialogTrigger>
                  <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                      <DialogTitle>Edit Deal Log</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                      <form
                        id="edit-deal-log-form"
                        noValidate
                        onSubmit={handleEditDealLogFormSubmit}
                      >
                        <FieldGroup>
                          <FieldSet>
                            <FieldLegend>Transaction</FieldLegend>
                            <FieldGroup>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel
                                    className="inline-block"
                                    htmlFor="transaction-name-field"
                                  >
                                    Transaction Name
                                    <span className="text-red-500">*</span>
                                  </FieldLabel>
                                  <Input
                                    defaultValue={
                                      dealLog['transaction-name-field'] ??
                                      data1.node.name ??
                                      undefined
                                    }
                                    id="transaction-name-field"
                                    name="transaction-name-field"
                                    readOnly
                                  />
                                </Field>
                                <ComboBox
                                  defaultValue={
                                    dealLog['industry-gics-6-field']
                                  }
                                  id="industry-gics-6-field"
                                  isRequired
                                  label="Industry (GICS 6)"
                                  name="industry-gics-6-field"
                                  options={data4.rvaCalculatorIndustries.map(
                                    industry => ({
                                      label: industry.label ?? '',
                                      value: industry.value ?? ''
                                    })
                                  )}
                                />
                                {/* <Field>
                                <FieldLabel htmlFor="transaction-type-field">
                                  Transaction Type
                                </FieldLabel>
                                <Input
                                  defaultValue={
                                    dealLog['transaction-type-field']
                                  }
                                  disabled
                                  id="transaction-type-field"
                                  name="transaction-type-field"
                                  placeholder="Enter Value"
                                />
                              </Field> */}
                              </div>
                              {/* <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="geography-field">
                                  Geography
                                </FieldLabel>
                                <Input
                                  defaultValue={dealLog['geography-field']}
                                  disabled
                                  id="geography-field"
                                  name="geography-field"
                                  placeholder="Enter Value"
                                />
                              </Field>

                            </div> */}
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="total-opportunity-size-field">
                                    Total Opportunity Size
                                  </FieldLabel>
                                  <InputGroup>
                                    <InputGroupAddon>
                                      <InputGroupText>$</InputGroupText>
                                    </InputGroupAddon>
                                    <NumberField
                                      disabled
                                      id="total-opportunity-size-field"
                                      min={0}
                                      name="total-opportunity-size-field"
                                      value={totalOpportunitySize ?? ''}
                                    />
                                  </InputGroup>
                                </Field>
                                <ComboBox
                                  defaultValue={
                                    dealLog['debt-security-type-field']
                                  }
                                  id="debt-security-type-field"
                                  isRequired
                                  label="Debt Security Type"
                                  name="debt-security-type-field"
                                  options={data3.debtSecurities.map(
                                    security => ({
                                      label: security.name,
                                      value: security.value
                                    })
                                  )}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <ComboBox
                                  defaultValue={
                                    dealLog['equity-security-type-field']
                                  }
                                  id="equity-security-type-field"
                                  isRequired
                                  label="Equity Security Type"
                                  name="equity-security-type-field"
                                  options={data2.equitySecurities.map(
                                    security => ({
                                      label: security.name,
                                      value: security.value
                                    })
                                  )}
                                />
                                <Field>
                                  <FieldLabel htmlFor="total-debt-opportunity-size-field">
                                    Total Debt Opportunity Size
                                  </FieldLabel>
                                  <InputGroup>
                                    <InputGroupAddon>
                                      <InputGroupText>$</InputGroupText>
                                    </InputGroupAddon>
                                    <NumberField
                                      defaultValue={
                                        dealLog[
                                          'total-debt-opportunity-size-field'
                                        ]
                                      }
                                      id="total-debt-opportunity-size-field"
                                      min={0}
                                      name="total-debt-opportunity-size-field"
                                      onChange={e => {
                                        setDealLog(prev => ({
                                          ...prev,
                                          'total-debt-opportunity-size-field':
                                            e.target.value || undefined
                                        }));
                                      }}
                                      placeholder="Enter Value"
                                    />
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="total-equity-opportunity-size-field">
                                    Total Equity Opportunity Size
                                  </FieldLabel>
                                  <InputGroup>
                                    <InputGroupAddon>
                                      <InputGroupText>$</InputGroupText>
                                    </InputGroupAddon>
                                    <NumberField
                                      defaultValue={
                                        dealLog[
                                          'total-equity-opportunity-size-field'
                                        ]
                                      }
                                      id="total-equity-opportunity-size-field"
                                      min={0}
                                      name="total-equity-opportunity-size-field"
                                      onChange={e => {
                                        setDealLog(prev => ({
                                          ...prev,
                                          'total-equity-opportunity-size-field':
                                            e.target.value || undefined
                                        }));
                                      }}
                                      placeholder="Enter Value"
                                    />
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="debt-%-of-invested-capital-field">
                                    Debt % of Invested Capital
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      disabled
                                      id="debt-%-of-invested-capital-field"
                                      max={100}
                                      min={0}
                                      name="debt-%-of-invested-capital-field"
                                      value={debtPercentOfInvestedCapital ?? ''}
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="equity-%-of-invested-capital">
                                    Equity % of Invested Capital
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      disabled
                                      id="equity-%-of-invested-capital"
                                      max={100}
                                      min={0}
                                      name="equity-%-of-invested-capital"
                                      value={
                                        equityPercentOfInvestedCapital ?? ''
                                      }
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="industry-percent-plug-field">
                                    Industry % Plug
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog['industry-percent-plug-field']
                                      }
                                      id="industry-percent-plug-field"
                                      max={100}
                                      min={0}
                                      name="industry-percent-plug-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="equity-percent-plug-field">
                                    Equity % Plug
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog['equity-percent-plug-field']
                                      }
                                      id="equity-percent-plug-field"
                                      max={100}
                                      min={0}
                                      name="equity-percent-plug-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              {/* <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="advance-rate-on-debt-fund-v-field">
                                  Advance Rate on Debt - Fund V
                                </FieldLabel>
                                <Input
                                  defaultValue={
                                    dealLog['advance-rate-on-debt-fund-v-field']
                                  }
                                  disabled
                                  id="advance-rate-on-debt-fund-v-field"
                                  name="advance-rate-on-debt-fund-v-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="fdo-field">FDO</FieldLabel>
                                <Input
                                  defaultValue={dealLog['fdo-field']}
                                  disabled
                                  id="fdo-field"
                                  name="fdo-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                            </div> */}
                            </FieldGroup>
                          </FieldSet>
                          <FieldSet>
                            <FieldLegend>Debt Deal Economics</FieldLegend>
                            <FieldGroup>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="sofr-field">
                                    SOFR
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={dealLog['sofr-field']}
                                      id="sofr-field"
                                      max={100}
                                      min={0}
                                      name="sofr-field"
                                      readOnly
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="post-fo-cash-spread-field">
                                    Post-FO Cash Spread
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog['post-fo-cash-spread-field']
                                      }
                                      id="post-fo-cash-spread-field"
                                      max={100}
                                      min={0}
                                      name="post-fo-cash-spread-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="pik-rate-field">
                                    PIK Rate
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={dealLog['pik-rate-field']}
                                      id="pik-rate-field"
                                      max={100}
                                      min={0}
                                      name="pik-rate-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="closing-fees-field">
                                    Closing Fees
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog['closing-fees-field']
                                      }
                                      id="closing-fees-field"
                                      max={100}
                                      min={0}
                                      name="closing-fees-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                            </FieldGroup>
                          </FieldSet>
                          <FieldSet>
                            <FieldLegend>Target Returns</FieldLegend>
                            <FieldGroup>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="gross-unlevered-debt-irr-field">
                                    Gross Unlevered Debt IRR
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog[
                                          'gross-unlevered-debt-irr-field'
                                        ]
                                      }
                                      id="gross-unlevered-debt-irr-field"
                                      max={100}
                                      min={0}
                                      name="gross-unlevered-debt-irr-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="gross-unlevered-equity-irr-field">
                                    Gross Unlevered Equity IRR
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog[
                                          'gross-unlevered-equity-irr-field'
                                        ]
                                      }
                                      id="gross-unlevered-equity-irr-field"
                                      max={100}
                                      min={0}
                                      name="gross-unlevered-equity-irr-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="gross-unlevered-blended-irr-field">
                                    Gross Unlevered Blended IRR
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog[
                                          'gross-unlevered-blended-irr-field'
                                        ]
                                      }
                                      id="gross-unlevered-blended-irr-field"
                                      max={100}
                                      min={0}
                                      name="gross-unlevered-blended-irr-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="gross-unlevered-cash-yield-blended-field">
                                    Gross Unlevered Cash Yield (Blended)
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        dealLog[
                                          'gross-unlevered-cash-yield-blended-field'
                                        ]
                                      }
                                      id="gross-unlevered-cash-yield-blended-field"
                                      max={100}
                                      min={0}
                                      name="gross-unlevered-cash-yield-blended-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2">
                                <Field>
                                  <FieldLabel htmlFor="total-moic-gross-unlevered-field">
                                    Total MOIC (Gross Unlevered)
                                  </FieldLabel>
                                  <NumberField
                                    defaultValue={
                                      dealLog[
                                        'total-moic-gross-unlevered-field'
                                      ]
                                    }
                                    id="total-moic-gross-unlevered-field"
                                    name="total-moic-gross-unlevered-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                              </div>
                            </FieldGroup>
                          </FieldSet>
                        </FieldGroup>
                      </form>
                    </DialogBody>
                    <DialogFooter>
                      <Button
                        form="edit-deal-log-form"
                        type="submit"
                      >
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <form
                id="deal-log-form"
                noValidate
                onSubmit={event => {
                  void handleDealLogFormSubmit(event);
                }}
              >
                <div className="space-y-5">
                  <Accordion.Root
                    collapsible
                    defaultValue={id}
                    type="single"
                  >
                    <Accordion.Item
                      className="bg-card rounded-md border p-5"
                      value={id}
                    >
                      <Accordion.Header className="space-y-2.5">
                        <Accordion.Trigger className="group flex w-full items-center gap-5">
                          <ChevronDownIcon
                            aria-hidden
                            className="transition-transform group-data-[state=open]:rotate-z-180"
                            size={16}
                          />
                          <div className="flex items-center gap-2">
                            <div className="text-lg font-semibold">
                              Transaction
                            </div>
                          </div>
                        </Accordion.Trigger>
                      </Accordion.Header>
                      <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 space-y-5 overflow-hidden">
                        <Separator />
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Transaction Name{' '}
                              <span className="text-red-500">*</span>
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['transaction-name-field'] ??
                                data1.node.name ??
                                '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Industry (GICS 6)
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {industryLabel ?? '-'}
                            </TruncatedTextTooltip>
                          </div>
                          {/* <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Transaction Type
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['transaction-type-field'] ?? '-'}
                            </TruncatedTextTooltip>
                          </div> */}
                        </div>
                        {/* <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Geography
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['geography-field'] ?? '-'}
                            </TruncatedTextTooltip>
                          </div>

                        </div> */}
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Total Opportunity Size
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {totalOpportunitySize
                                ? `$${totalOpportunitySize}`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Debt Security Type{' '}
                              <span className="text-red-500">*</span>
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {debtSecurityTypeLabel ?? '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Equity Security Type{' '}
                              <span className="text-red-500">*</span>
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {equitySecurityTypeLabel ?? '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Total Debt Opportunity Size
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['total-debt-opportunity-size-field']
                                ? `$${dealLog['total-debt-opportunity-size-field']}`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Total Equity Opportunity Size
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['total-equity-opportunity-size-field']
                                ? `$${dealLog['total-equity-opportunity-size-field']}`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Debt % of Invested Capital
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {debtPercentOfInvestedCapital
                                ? `${debtPercentOfInvestedCapital}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Equity % of Invested Capital
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {equityPercentOfInvestedCapital
                                ? `${equityPercentOfInvestedCapital}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Industry % Plug
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['industry-percent-plug-field']
                                ? `${dealLog['industry-percent-plug-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Equity % Plug
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['equity-percent-plug-field']
                                ? `${dealLog['equity-percent-plug-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        {/* <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Advance Rate on Debt - Fund V
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['advance-rate-on-debt-fund-v-field'] ??
                                '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              FDO
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['fdo-field'] ?? '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div> */}
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion.Root>
                  <Accordion.Root
                    collapsible
                    defaultValue={id}
                    type="single"
                  >
                    <Accordion.Item
                      className="bg-card rounded-md border p-5"
                      value={id}
                    >
                      <Accordion.Header className="space-y-2.5">
                        <Accordion.Trigger className="group flex w-full items-center gap-5">
                          <ChevronDownIcon
                            aria-hidden
                            className="transition-transform group-data-[state=open]:rotate-z-180"
                            size={16}
                          />
                          <div className="flex items-center gap-2">
                            <div className="text-lg font-semibold">
                              Debt Deal Economics
                            </div>
                          </div>
                        </Accordion.Trigger>
                      </Accordion.Header>
                      <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 space-y-5 overflow-hidden">
                        <Separator />
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              SOFR
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['sofr-field']
                                ? `${dealLog['sofr-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Post-FO Cash Spread
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['post-fo-cash-spread-field']
                                ? `${dealLog['post-fo-cash-spread-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              PIK Rate
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['pik-rate-field']
                                ? `${dealLog['pik-rate-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Closing Fees
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['closing-fees-field']
                                ? `${dealLog['closing-fees-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion.Root>
                  <Accordion.Root
                    collapsible
                    defaultValue={id}
                    type="single"
                  >
                    <Accordion.Item
                      className="bg-card rounded-md border p-5"
                      value={id}
                    >
                      <Accordion.Header className="space-y-2.5">
                        <Accordion.Trigger className="group flex w-full items-center gap-5">
                          <ChevronDownIcon
                            aria-hidden
                            className="transition-transform group-data-[state=open]:rotate-z-180"
                            size={16}
                          />
                          <div className="flex items-center gap-2">
                            <div className="text-lg font-semibold">
                              Target Returns
                            </div>
                          </div>
                        </Accordion.Trigger>
                      </Accordion.Header>
                      <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 space-y-5 overflow-hidden">
                        <Separator />
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Gross Unlevered Debt IRR
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['gross-unlevered-debt-irr-field']
                                ? `${dealLog['gross-unlevered-debt-irr-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Gross Unlevered Equity IRR
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['gross-unlevered-equity-irr-field']
                                ? `${dealLog['gross-unlevered-equity-irr-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Gross Unlevered Blended IRR
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['gross-unlevered-blended-irr-field']
                                ? `${dealLog['gross-unlevered-blended-irr-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Gross Unlevered Cash Yield (Blended)
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog[
                                'gross-unlevered-cash-yield-blended-field'
                              ]
                                ? `${dealLog['gross-unlevered-cash-yield-blended-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-5">
                          <div className="flex items-center gap-x-2.5 pr-[30px]">
                            <div className="text-foreground w-[300px] text-sm">
                              Total MOIC (Gross Unlevered)
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['total-moic-gross-unlevered-field']
                                ? `${dealLog['total-moic-gross-unlevered-field']}%`
                                : '-'}
                            </TruncatedTextTooltip>
                          </div>
                          <div className="flex items-center gap-x-2.5 pr-[30px] opacity-0">
                            <div className="text-foreground w-[300px] text-sm">
                              Closing Fees
                            </div>
                            <TruncatedTextTooltip className="text-muted-foreground text-sm font-medium">
                              {dealLog['closing-fees-field'] ?? '-'}
                            </TruncatedTextTooltip>
                          </div>
                        </div>
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion.Root>
                </div>
              </form>
              <div className="flex justify-end">
                <Button
                  disabled={
                    isCreatingRvaCalculator || isComputingFundSpecificRva
                  }
                  form="deal-log-form"
                  type="submit"
                >
                  {(isCreatingRvaCalculator || isComputingFundSpecificRva) && (
                    <Spinner />
                  )}
                  Next
                  <ArrowRightIcon />
                </Button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">Portfolio Input</div>
                <Dialog
                  onOpenChange={setEditPortfolioInputDialogIsOpen}
                  open={editPortfolioInputDialogIsOpen}
                >
                  <DialogTrigger asChild>
                    <EditButton />
                  </DialogTrigger>
                  <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                      <DialogTitle>Edit Portfolio Input</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                      <form
                        id="edit-portfolio-input-form"
                        noValidate
                        onSubmit={handleEditPortfolioInputFormSubmit}
                      >
                        <FieldGroup>
                          {fundNames.map(fund => {
                            const slug = fundNameToSlug(fund);
                            return (
                              <FieldSet key={fund}>
                                <FieldLegend>{fund}</FieldLegend>
                                <FieldGroup>
                                  <div className="space-y-2.5">
                                    <div className="text-sm font-semibold">
                                      Current Concentration
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-2">
                                      <Field>
                                        <FieldLabel
                                          htmlFor={`${slug}-current-concentration-range-low-end-field`}
                                        >
                                          Range - Low End
                                        </FieldLabel>
                                        <InputGroup>
                                          <NumberField
                                            defaultValue={
                                              portfolioInput[
                                                `${slug}-current-concentration-range-low-end-field`
                                              ]
                                            }
                                            id={`${slug}-current-concentration-range-low-end-field`}
                                            max={100}
                                            min={0}
                                            name={`${slug}-current-concentration-range-low-end-field`}
                                            onBlur={event =>
                                              void handleConcentrationLimitLowEndBlur(
                                                event
                                              )
                                            }
                                            placeholder="Enter Value"
                                          />
                                          <InputGroupAddon align="inline-end">
                                            <InputGroupText>%</InputGroupText>
                                          </InputGroupAddon>
                                        </InputGroup>
                                      </Field>
                                      <Field>
                                        <FieldLabel
                                          htmlFor={`${slug}-current-concentration-range-high-end-field`}
                                        >
                                          Range - High End
                                        </FieldLabel>
                                        <InputGroup>
                                          <NumberField
                                            defaultValue={
                                              portfolioInput[
                                                `${slug}-current-concentration-range-high-end-field`
                                              ]
                                            }
                                            id={`${slug}-current-concentration-range-high-end-field`}
                                            max={100}
                                            min={0}
                                            name={`${slug}-current-concentration-range-high-end-field`}
                                            onBlur={event =>
                                              void handleConcentrationLimitHighEndBlur(
                                                event
                                              )
                                            }
                                            placeholder="Enter Value"
                                          />
                                          <InputGroupAddon align="inline-end">
                                            <InputGroupText>%</InputGroupText>
                                          </InputGroupAddon>
                                        </InputGroup>
                                      </Field>
                                    </div>
                                  </div>
                                  <div className="space-y-2.5">
                                    <div className="text-sm font-semibold">
                                      Debt - Current Concentration
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-2">
                                      <Field>
                                        <FieldLabel
                                          htmlFor={`${slug}-debt-current-concentration-range-low-end-field`}
                                        >
                                          Range - Low End
                                        </FieldLabel>
                                        <InputGroup>
                                          <NumberField
                                            defaultValue={
                                              portfolioInput[
                                                `${slug}-debt-current-concentration-range-low-end-field`
                                              ]
                                            }
                                            id={`${slug}-debt-current-concentration-range-low-end-field`}
                                            max={100}
                                            min={0}
                                            name={`${slug}-debt-current-concentration-range-low-end-field`}
                                            onBlur={event =>
                                              void handleConcentrationLimitLowEndBlur(
                                                event
                                              )
                                            }
                                            placeholder="Enter Value"
                                          />
                                          <InputGroupAddon align="inline-end">
                                            <InputGroupText>%</InputGroupText>
                                          </InputGroupAddon>
                                        </InputGroup>
                                      </Field>
                                      <Field>
                                        <FieldLabel
                                          htmlFor={`${slug}-debt-current-concentration-range-high-end-field`}
                                        >
                                          Range - High End
                                        </FieldLabel>
                                        <InputGroup>
                                          <NumberField
                                            defaultValue={
                                              portfolioInput[
                                                `${slug}-debt-current-concentration-range-high-end-field`
                                              ]
                                            }
                                            id={`${slug}-debt-current-concentration-range-high-end-field`}
                                            max={100}
                                            min={0}
                                            name={`${slug}-debt-current-concentration-range-high-end-field`}
                                            onBlur={event =>
                                              void handleConcentrationLimitHighEndBlur(
                                                event
                                              )
                                            }
                                            placeholder="Enter Value"
                                          />
                                          <InputGroupAddon align="inline-end">
                                            <InputGroupText>%</InputGroupText>
                                          </InputGroupAddon>
                                        </InputGroup>
                                      </Field>
                                    </div>
                                  </div>
                                  <div className="space-y-2.5">
                                    <div className="text-sm font-semibold">
                                      Equity - Current Concentration
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-2">
                                      <Field>
                                        <FieldLabel
                                          htmlFor={`${slug}-equity-current-concentration-range-low-end-field`}
                                        >
                                          Range - Low End
                                        </FieldLabel>
                                        <InputGroup>
                                          <NumberField
                                            defaultValue={
                                              portfolioInput[
                                                `${slug}-equity-current-concentration-range-low-end-field`
                                              ]
                                            }
                                            id={`${slug}-equity-current-concentration-range-low-end-field`}
                                            max={100}
                                            min={0}
                                            name={`${slug}-equity-current-concentration-range-low-end-field`}
                                            onBlur={event =>
                                              void handleConcentrationLimitLowEndBlur(
                                                event
                                              )
                                            }
                                            placeholder="Enter Value"
                                          />
                                          <InputGroupAddon align="inline-end">
                                            <InputGroupText>%</InputGroupText>
                                          </InputGroupAddon>
                                        </InputGroup>
                                      </Field>
                                      <Field>
                                        <FieldLabel
                                          htmlFor={`${slug}-equity-current-concentration-range-high-end-field`}
                                        >
                                          Range - High End
                                        </FieldLabel>
                                        <InputGroup>
                                          <NumberField
                                            defaultValue={
                                              portfolioInput[
                                                `${slug}-equity-current-concentration-range-high-end-field`
                                              ]
                                            }
                                            id={`${slug}-equity-current-concentration-range-high-end-field`}
                                            max={100}
                                            min={0}
                                            name={`${slug}-equity-current-concentration-range-high-end-field`}
                                            onBlur={event =>
                                              void handleConcentrationLimitHighEndBlur(
                                                event
                                              )
                                            }
                                            placeholder="Enter Value"
                                          />
                                          <InputGroupAddon align="inline-end">
                                            <InputGroupText>%</InputGroupText>
                                          </InputGroupAddon>
                                        </InputGroup>
                                      </Field>
                                    </div>
                                  </div>
                                </FieldGroup>
                              </FieldSet>
                            );
                          })}
                        </FieldGroup>
                      </form>
                    </DialogBody>
                    <DialogFooter>
                      <Button
                        form="edit-portfolio-input-form"
                        type="submit"
                      >
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <form
                id="portfolio-input-form"
                noValidate
                onSubmit={event => {
                  void handlePortfolioInputFormSubmit(event);
                }}
              >
                <div className="space-y-5">
                  {fundNames.map(fund => (
                    <Accordion.Root
                      collapsible
                      defaultValue={fund}
                      key={fund}
                      type="single"
                    >
                      <Accordion.Item
                        className="bg-card rounded-md border p-5"
                        value={fund}
                      >
                        <Accordion.Header className="space-y-2.5">
                          <Accordion.Trigger className="group flex w-full items-center gap-5">
                            <ChevronDownIcon
                              aria-hidden
                              className="transition-transform group-data-[state=open]:rotate-z-180"
                              size={16}
                            />
                            <div className="flex items-center gap-2">
                              <div className="text-lg font-semibold">
                                {fund}
                              </div>
                            </div>
                          </Accordion.Trigger>
                        </Accordion.Header>
                        <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 space-y-5 overflow-hidden">
                          <Separator />
                          <div className="space-y-2.5">
                            <div className="text-sm font-semibold">
                              Current Concentration
                            </div>
                            <div className="grid grid-cols-2 gap-x-5">
                              <div className="flex items-center gap-x-2.5 pr-[30px]">
                                <div className="text-foreground w-[300px] text-sm">
                                  Range - Low End
                                </div>
                                <div className="text-muted-foreground text-sm font-medium">
                                  {portfolioInput[
                                    `${fundNameToSlug(fund)}-current-concentration-range-low-end-field`
                                  ] ?? '-'}
                                </div>
                              </div>
                              <div className="flex items-center gap-x-2.5 pr-[30px]">
                                <div className="text-foreground w-[300px] text-sm">
                                  Range - High End
                                </div>
                                <div className="text-muted-foreground text-sm font-medium">
                                  {portfolioInput[
                                    `${fundNameToSlug(fund)}-current-concentration-range-high-end-field`
                                  ] ?? '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2.5">
                            <div className="text-sm font-semibold">
                              Debt - Current Concentration
                            </div>
                            <div className="grid grid-cols-2 gap-x-5">
                              <div className="flex items-center gap-x-2.5 pr-[30px]">
                                <div className="text-foreground w-[300px] text-sm">
                                  Range - Low End
                                </div>
                                <div className="text-muted-foreground text-sm font-medium">
                                  {portfolioInput[
                                    `${fundNameToSlug(fund)}-debt-current-concentration-range-low-end-field`
                                  ] ?? '-'}
                                </div>
                              </div>
                              <div className="flex items-center gap-x-2.5 pr-[30px]">
                                <div className="text-foreground w-[300px] text-sm">
                                  Range - High End
                                </div>
                                <div className="text-muted-foreground text-sm font-medium">
                                  {portfolioInput[
                                    `${fundNameToSlug(fund)}-debt-current-concentration-range-high-end-field`
                                  ] ?? '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2.5">
                            <div className="text-sm font-semibold">
                              Equity - Current Concentration
                            </div>
                            <div className="grid grid-cols-2 gap-x-5">
                              <div className="flex items-center gap-x-2.5 pr-[30px]">
                                <div className="text-foreground w-[300px] text-sm">
                                  Range - Low End
                                </div>
                                <div className="text-muted-foreground text-sm font-medium">
                                  {portfolioInput[
                                    `${fundNameToSlug(fund)}-equity-current-concentration-range-low-end-field`
                                  ] ?? '-'}
                                </div>
                              </div>
                              <div className="flex items-center gap-x-2.5 pr-[30px]">
                                <div className="text-foreground w-[300px] text-sm">
                                  Range - High End
                                </div>
                                <div className="text-muted-foreground text-sm font-medium">
                                  {portfolioInput[
                                    `${fundNameToSlug(fund)}-equity-current-concentration-range-high-end-field`
                                  ] ?? '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion.Root>
                  ))}
                </div>
              </form>
              <div className="flex justify-between">
                <Button
                  onClick={handleBackButtonClick}
                  variant="secondary"
                >
                  <ArrowLeftIcon />
                  Back
                </Button>
                <Button
                  disabled={loading1}
                  form="portfolio-input-form"
                  type="submit"
                >
                  {loading1 && <Spinner />}
                  Next
                  <ArrowRightIcon />
                </Button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5">
              <Tabs defaultValue="summary">
                <TabsList className="w-full">
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="calculations">Calculations</TabsTrigger>
                </TabsList>
                <TabsContent value="summary">
                  <div className="space-y-5">
                    <div className="bg-card rounded-md border p-5">
                      <DataTable
                        columns={fundNames}
                        label="Assumptions"
                        labelKey="Assumptions"
                        rows={dataTable1}
                      />
                    </div>
                    <div className="bg-card rounded-md border p-5">
                      <DataTable
                        columns={fundNames}
                        label="Additional Considerations"
                        labelKey="Additional Considerations"
                        rows={dataTable2}
                      />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="calculations">
                  <div className="space-y-5">
                    <Accordion.Root
                      defaultValue={[
                        'fund-assumptions',
                        'step-1',
                        'step-2',
                        'step-3',
                        'concentration-test',
                        'relative-value-analysis'
                      ]}
                      type="multiple"
                    >
                      <div className="space-y-5">
                        <CalculationAccordionItem
                          title="Fund Assumptions"
                          value="fund-assumptions"
                        >
                          <DataTable
                            columns={fundNames}
                            label="Fund Assumptions"
                            labelKey="Fund Assumptions"
                            rows={dataTable3}
                          />
                        </CalculationAccordionItem>
                        <CalculationAccordionItem
                          title="Step 1: Asset Yield"
                          value="step-1"
                        >
                          <DataTable
                            columns={fundNames}
                            label="Step 1: Asset Yield"
                            labelKey="Step 1: Asset Yield"
                            rows={dataTable4}
                          />
                        </CalculationAccordionItem>
                        <CalculationAccordionItem
                          title="Step 2: Net Income Generation"
                          value="step-2"
                        >
                          <DataTable
                            columns={fundNames}
                            label="Step 2: Net Income Generation"
                            labelKey="Step 2: Net Income Generation"
                            rows={dataTable5}
                          />
                        </CalculationAccordionItem>
                        <CalculationAccordionItem
                          title="Step 3: Carry"
                          value="step-3"
                        >
                          <DataTable
                            columns={fundNames}
                            label="Step 3: Carry"
                            labelKey="Step 3: Carry"
                            rows={dataTable6}
                          />
                        </CalculationAccordionItem>
                        <CalculationAccordionItem
                          title="Concentration Test"
                          value="concentration-test"
                        >
                          <DataTable
                            columns={fundNames}
                            label="Concentration Test"
                            labelKey="Concentration Test"
                            rows={dataTable7}
                          />
                        </CalculationAccordionItem>
                        <CalculationAccordionItem
                          title="Relative Value Analysis"
                          value="relative-value-analysis"
                        >
                          <DataTable
                            columns={fundNames}
                            label="Relative Value Analysis"
                            labelKey="Relative Value Analysis"
                            rows={dataTable8}
                          />
                        </CalculationAccordionItem>
                      </div>
                    </Accordion.Root>
                  </div>
                </TabsContent>
              </Tabs>
              {matchNewCalculationPage && (
                <div className="flex justify-between">
                  <Button
                    onClick={handleBackButtonClick}
                    variant="secondary"
                  >
                    <ArrowLeftIcon />
                    Back
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Wrapper;
